import * as React from 'react'
import { Upload, Trash2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { authService } from '@/services/AuthService'
import { uploadService, UploadsUnavailableError } from '@/services/UploadService'
import { fileToResizedDataUrl } from '@/lib/image'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { changePasswordInputSchema } from '@lib/input/user.input'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { FormActions } from '@/components/ui/form-actions'
import { FormError } from '@/components/ui/form-error'
import { AppearanceOptions } from '@/components/ThemeMenu'
import { ApiTokensSection } from '@/components/ApiTokensSection'
import { AgentsSection } from '@/components/AgentsSection'
import { accountLabelOf, tokenScopesOf } from '@/auth/access'

function initials(nameOrEmail: string): string {
  const base = nameOrEmail.includes('@') ? (nameOrEmail.split('@')[0] ?? '') : nameOrEmail
  const parts = base.split(/[.\s_+-]+/).filter(Boolean)
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}

function requestErrorMessage<E>(error: E): string {
  if (error instanceof UploadsUnavailableError) return 'Photo uploads aren’t available right now.'
  if (isApiError(error)) return error.body.message ?? `Request failed (status ${error.status}).`
  return toErrorMessage(error)
}

/** Avatar + display name — quiet sections, no elevated cards. */
function ProfileSection() {
  const { user, updateProfile } = useAuth()
  const [name, setName] = React.useState(user?.name ?? '')
  const [savingName, setSavingName] = React.useState(false)
  const [avatarBusy, setAvatarBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [nameSaved, setNameSaved] = React.useState(false)
  const fileRef = React.useRef<HTMLInputElement>(null)

  if (!user) return null
  // Narrow for async handlers — `user` from hooks may be null again after await.
  const me = user

  const nameDirty = name.trim() !== '' && name.trim() !== me.name

  async function onPickFile(file: File): Promise<void> {
    setError(null)
    setAvatarBusy(true)
    try {
      const dataUrl = await fileToResizedDataUrl(file, 256)
      const { key } = await uploadService.uploadImage(dataUrl)
      await updateProfile({ avatarUrl: key })
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setAvatarBusy(false)
    }
  }

  async function removeAvatar(): Promise<void> {
    setError(null)
    setAvatarBusy(true)
    try {
      await updateProfile({ avatarUrl: null })
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setAvatarBusy(false)
    }
  }

  async function saveName(): Promise<void> {
    const trimmed = name.trim()
    if (!trimmed || trimmed === me.name) return
    setError(null)
    setNameSaved(false)
    setSavingName(true)
    try {
      await updateProfile({ name: trimmed })
      setNameSaved(true)
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setSavingName(false)
    }
  }

  return (
    <section className="space-y-6">
      <SectionHeader
        title="Profile"
        description="Your photo and name show up for collaborators on live floorplans."
      />

      <div className="flex flex-wrap items-center gap-4">
        {me.avatarUrl ? (
          <img
            src={me.avatarUrl}
            alt=""
            className="h-16 w-16 shrink-0 rounded-round object-cover ring-1 ring-line"
          />
        ) : (
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-round bg-past-soft text-lg font-medium text-ink-muted ring-1 ring-line">
            {initials(me.name || me.email)}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void onPickFile(file)
              e.target.value = ''
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            pending={avatarBusy}
          >
            <Upload className="h-4 w-4" />
            {me.avatarUrl ? 'Change photo' : 'Upload photo'}
          </Button>
          {me.avatarUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={avatarBusy}
              onClick={() => void removeAvatar()}
            >
              <Trash2 className="h-4 w-4" /> Remove
            </Button>
          ) : null}
        </div>
      </div>

      <Field label="Name" htmlFor="profile-name">
        <div className="flex max-w-md flex-wrap items-center gap-2">
          <Input
            id="profile-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setNameSaved(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && nameDirty) void saveName()
            }}
            className="min-w-[12rem] flex-1"
            autoComplete="name"
          />
          <Button
            type="button"
            size="sm"
            onClick={() => void saveName()}
            disabled={!nameDirty}
            pending={savingName}
          >
            Save
          </Button>
        </div>
      </Field>

      <div className="space-y-0.5 text-sm text-ink-muted">
        <p>{me.email}</p>
        <p>{accountLabelOf(me)}</p>
      </div>

      {error ? <FormError>{error}</FormError> : null}
      {nameSaved && !error ? <p className="text-sm text-ink-muted">Name saved.</p> : null}
    </section>
  )
}

/** Change your own password while signed in. */
function PasswordSection() {
  const [current, setCurrent] = React.useState('')
  const [next, setNext] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [done, setDone] = React.useState(false)

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setDone(false)
    if (next !== confirm) {
      setError('New passwords don’t match.')
      return
    }
    const parsed = changePasswordInputSchema.safeParse({
      currentPassword: current,
      newPassword: next,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the fields and try again.')
      return
    }
    setBusy(true)
    try {
      await authService.changePassword(parsed.data)
      setCurrent('')
      setNext('')
      setConfirm('')
      setDone(true)
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="space-y-4">
      <SectionHeader title="Password" description="Change the password you use to sign in." />
      <form className="max-w-sm space-y-4" onSubmit={(e) => void submit(e)} noValidate>
        <Field label="Current password" htmlFor="current-password">
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field label="New password" htmlFor="new-password">
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <Field label="Confirm new password" htmlFor="confirm-password">
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        {error ? <FormError>{error}</FormError> : null}
        {done ? <p className="text-sm text-ink-muted">Password updated.</p> : null}
        <FormActions align="start">
          <Button pending={busy} type="submit" disabled={!current || !next}>
            Change password
          </Button>
        </FormActions>
      </form>
    </section>
  )
}

/** Appearance: system / light / dark (same options as the sidebar control). */
function AppearanceSection() {
  return (
    <section className="space-y-3">
      <SectionHeader
        title="Appearance"
        description="Use the system setting, or lock light or dark."
      />
      <AppearanceOptions className="max-w-xs" />
    </section>
  )
}

/**
 * Self-service account settings: avatar, display name, password, appearance.
 * Flat sections + hairlines (Notion-style density), not stacked cards.
 */
export function ProfilePage() {
  const { user } = useAuth()
  // Connecting an assistant makes a token, so both sections are for the roles
  // that can hold one; for anyone else neither shows, and nor do their rules.
  const mayConnect = tokenScopesOf(user).length > 0
  return (
    <PageContainer className="space-y-8">
      <PageHeader
        title="Your profile"
        description={
          mayConnect
            ? 'Manage your photo, name, password, appearance, connected assistants, and API tokens.'
            : 'Manage your photo, name, password, and appearance.'
        }
      />
      <ProfileSection />
      <div className="border-t border-line" />
      <AppearanceSection />
      <div className="border-t border-line" />
      <PasswordSection />
      {mayConnect && (
        <>
          <div className="border-t border-line" />
          <AgentsSection />
          <div className="border-t border-line" />
          <ApiTokensSection />
        </>
      )}
    </PageContainer>
  )
}
